import {
  ButtonGroup,
  colors,
  Container,
  Divider,
  Tab,
  Tabs,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import Page from "src/components/Page";
import PageHeader from "src/components/PageHeader";
import { getDevice, getDeviceTelemDispCfgs } from "src/repos/devices";
import { isSuperUser } from "../../../../utils/session";
import Analytics from "./Analytics";
import Anomalies from "./Anomalies";
import Configuration from "./Configuration";
import CurrentState from "./CurrentState";
import Events from "./Events";
import Maintenance from "./Maintenance";
import Overview from "./Overview";
import Samplers from "./Samplers";
import Alarms from "./Alarms";
import Comments from "./Comments";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  root: {},
  divider: {
    backgroundColor: colors.grey[300],
  },
  tabsWrapper: {
    display: "flex",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      flexFlow: "wrap",
      alignItems: "flex-start",
    },
  },
  commentSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
  },
  commentInput: {
    marginBottom: theme.spacing(2),
  },
  commentList: {
    marginTop: theme.spacing(2),
  },
  contentWithComments: {
    display: "flex",
    marginTop: theme.spacing(3),
  },
  mainContent: {
    flex: 1,
    marginRight: theme.spacing(3),
  },
  commentsPanel: {
    width: 350,
    minWidth: 350,
  },
}));

function MachineDetails() {
  const history = useHistory();
  const classes = useStyles();
  const { id, tab } = useParams();
  const [device, setDevice] = useState({});
  const [createdEvent] = useState({});
  const isUnconfigured = device.facility_id === null;
  const { t } = useTranslation(["glossary", "common"]);

  useEffect(() => {
    getDevice(id)
      .then((r) => r.json())
      .then((d) => {
        setDevice(d.data);
        getDeviceTelemDispCfgs(d.data.id);
      });
  }, [id]);

  const handleTabsChange = (event, value) => {
    history.replace(`/machines/${id}/${value}`);
  };

  const isSU = isSuperUser();
  const superUserOnlyTabs = isSU
    ? [{ value: "configuration", label: t("configuration"), disabled: false }]
    : [];

  const tabs = [
    { value: "overview", label: t("overview"), disabled: isUnconfigured },
    { value: "maintenance", label: t("maintenance"), disabled: isUnconfigured },
    { value: "analytics", label: t("analytics"), disabled: isUnconfigured },
    {
      value: "anomalies",
      label: t("anomaly_explorer"),
      disabled: isUnconfigured,
    },
    { value: "events", label: t("events"), disabled: isUnconfigured },
    {
      value: "machine_health",
      label: t("machine_health"),
      disabled: isUnconfigured,
    },
    { value: "alarms", label: t("alarms"), disabled: isUnconfigured },
    { value: "comments", label: t("comments"), disabled: isUnconfigured },
    ...superUserOnlyTabs,
    { value: "samplers", label: "Samplers", disabled: isUnconfigured },
  ];

  if (isUnconfigured && tab !== "configuration")
    return <Redirect to={`/machines/${id}/configuration`} />;
  else if (!tab) return <Redirect to={`/machines/${id}/overview`} />;

  if (!tabs.find((t) => t.value === tab)) {
    return <Redirect to="/errors/error-404" />;
  }

  return (
    <Page className={classes.root} title={tab.label}>
      <Container maxWidth="xl">
        <PageHeader
          pageTitle={t("machine_details")}
          machine={device}
          loading={_.isEmpty(device)}
        />
        <div className={classes.tabsWrapper}>
          <ButtonGroup></ButtonGroup>
          <Tabs
            indicatorColor="primary"
            onChange={handleTabsChange}
            scrollButtons="auto"
            value={tab}
            variant="scrollable"
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                label={tab.label}
                value={tab.value}
                disabled={tab.disabled}
              />
            ))}
          </Tabs>
        </div>
        <Divider className={classes.divider} />
        <div className={classes.contentWithComments}>
          <div className={classes.mainContent}>
            {tab === "overview" && <Overview machine={device} />}
            {tab === "machine_health" && <CurrentState machine={device} />}
            {tab === "maintenance" && <Maintenance machine={device} />}
            {tab === "anomalies" && (
              <Anomalies
                referrer={`/machines/${id}/anomalies`}
                device={device}
              />
            )}
            {tab === "events" && <Events createdEvent={createdEvent} device={device} />}
            {tab === "analytics" && <Analytics machine={device} />}
            {tab === "configuration" && (
              <Configuration machine={device} setMachine={setDevice} />
            )}
            {tab === "samplers" && (
              <Samplers machine={device} setMachine={setDevice} />
            )}
            {tab === "alarms" && (
              <Alarms machine={device} setMachine={setDevice} />
            )}
            {tab === "comments" && <Comments machine={device} />}
          </div>
        </div>
      </Container>
    </Page>
  );
}

MachineDetails.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default MachineDetails;